import { Box, Fade, IconButton, Paper, Stack, TextField, Typography, useTheme } from '@mui/material';
import { CloseRounded, MenuRounded } from '@mui/icons-material';

import { useIMCContext } from 'modules/IMC/common/context';
import HelpAgentIcon from 'modules/common/components/Icons/HelpAgentIcon';

const WIDGET_WIDTH = 380;
const WIDGET_HEIGHT = 750;

/**
 * Demo stand-in for the real HelpAgentWidget. Prod mounts that via
 * HelpAgentWidgetContainer in modules/App; DemoApp doesn't, and the real widget
 * embeds Decagon's chat which the demo can't load. This panel listens to the
 * same IMC context so "Get Help" still opens something — styled like the real
 * Decagon window (dark header, "Chat with Collective" title) but with the chat
 * gated behind a members-only message and a disabled input.
 */
export default function DemoHelpAgentWidget() {
    const theme = useTheme();
    const { isHelpAgentWidgetOpen, closeHelpAgentWidget } = useIMCContext();

    return (
        <Fade in={isHelpAgentWidgetOpen}>
            <Paper
                elevation={8}
                sx={{
                    position: 'fixed',
                    bottom: { xs: 0, md: 20 },
                    right: { xs: 0, md: 22 },
                    width: { xs: '100%', md: WIDGET_WIDTH },
                    height: { xs: '100%', md: `min(${WIDGET_HEIGHT}px, calc(100vh - 40px))` },
                    maxHeight: { xs: '100%', md: 'calc(100vh - 40px)' },
                    display: isHelpAgentWidgetOpen ? 'flex' : 'none',
                    flexDirection: 'column',
                    borderRadius: { xs: 0, md: 3 },
                    overflow: 'hidden',
                    boxShadow: 10,
                    zIndex: theme.zIndex.modal + 2,
                    bgcolor: 'background.paper',
                }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ bgcolor: 'grey.900', px: 2, py: 1.5 }}>
                    <MenuRounded sx={{ color: 'common.white' }} />
                    <Typography
                        variant="h6"
                        sx={{ flex: 1, textAlign: 'center', color: 'common.white', fontWeight: 700 }}>
                        Chat with Collective (BETA)
                    </Typography>
                    <IconButton aria-label="Close" size="small" onClick={() => closeHelpAgentWidget()}>
                        <CloseRounded sx={{ color: 'common.white' }} />
                    </IconButton>
                </Stack>

                <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ flex: 1, px: 4 }}>
                    <HelpAgentIcon sx={{ fontSize: 48 }} />
                    <Typography variant="h5" sx={{ textAlign: 'center' }}>
                        Chat support is a member feature
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Become a Collective member to chat with our team anytime and get answers about your bookkeeping,
                        taxes, and payroll.
                    </Typography>
                </Stack>

                <Box sx={{ p: 2 }}>
                    <TextField
                        fullWidth
                        disabled
                        variant="outlined"
                        size="small"
                        placeholder="Ask me anything, I'm here to help"
                    />
                </Box>
            </Paper>
        </Fade>
    );
}
